import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import '../styles/LoginPage.css';

export function calendarPathFromSearch(search) {
  const date = new URLSearchParams(search).get('date');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return '/';
  const [year, month, day] = date.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  const isValid = (
    parsed.getFullYear() === year
    && parsed.getMonth() === month - 1
    && parsed.getDate() === day
  );
  return isValid ? `/?date=${date}` : '/';
}

export default function LoginPage() {
  const [step, setStep] = useState('phone'); // phone | code
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return toast.error('أدخل رقم الهاتف');
    setLoading(true);
    try {
      const { data } = await api.post('/backoffice/auth/send-code', { phone });
      if (data.success) {
        toast.success('تم إرسال كود التحقق');
        // The server resolves a locally typed number to its stored E.164 form,
        // so verify must send that back rather than the raw input.
        if (data.phone) setPhone(data.phone);
        setRemainingAttempts(3);
        setCode('');
        setStep('code');
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  const resetToPhone = () => {
    setStep('phone');
    setCode('');
    setRemainingAttempts(3);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error('أدخل كود التحقق');
    setLoading(true);
    try {
      const { data } = await api.post('/backoffice/auth/verify-code', { phone, code });
      if (data.success) {
        const userRole = data.role || 'backoffice';
        const accountData = userRole === 'superadmin' ? data.superadmin : data.account;
        login(data.access_token, accountData, userRole);
        toast.success(data.message);
        navigate(
          userRole === 'superadmin'
            ? '/superadmin'
            : calendarPathFromSearch(location.search)
        );
      } else {
        if (data.max_attempts_reached) {
          toast.error('تم استنفاد جميع المحاولات');
          setRemainingAttempts(0);
          setTimeout(resetToPhone, 2000);
        } else {
          if (data.remaining_attempts !== undefined) {
            setRemainingAttempts(data.remaining_attempts);
          }
          toast.error(data.message);
        }
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.max_attempts_reached) {
        toast.error('تم استنفاد جميع المحاولات');
        setRemainingAttempts(0);
        setTimeout(resetToPhone, 2000);
      } else {
        if (errData?.remaining_attempts !== undefined) {
          setRemainingAttempts(errData.remaining_attempts);
        }
        toast.error(errData?.message || 'حدث خطأ');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">&#9881;</div>
          <h1>لوحة التحكم</h1>
          <p>تسجيل دخول المسؤول</p>
        </div>

        {step === 'phone' ? (
          <form onSubmit={handleSendCode}>
            <label htmlFor="phone">رقم الهاتف</label>
            <input
              id="phone"
              type="tel"
              placeholder="05XXXXXXXX أو +20XXXXXXXXX"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              dir="ltr"
              autoFocus
            />
            <button type="submit" disabled={loading}>
              {loading ? 'جاري الإرسال...' : 'إرسال كود التحقق'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode}>
            <p className="phone-display">{phone}</p>
            <label htmlFor="code">كود التحقق</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="------"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              dir="ltr"
              className="code-input"
              autoFocus
            />
            <div className={`bo-retry-counter ${remainingAttempts <= 1 ? 'bo-retry-warning' : ''}`}>
              محاولات متبقية: <span>{remainingAttempts}</span> / 3
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'جاري التحقق...' : 'تسجيل الدخول'}
            </button>
            <button type="button" className="link-btn" onClick={resetToPhone}>
              تغيير رقم الهاتف
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
